import type { Structure, VariantsDataMap,
	Component,
	GridItemLayout,
	LayoutData,
	SingleLayoutData,
	SingleLayoutItem,
	UnresolvedBreakpointsData,
} from '@wix/thunderbolt-becky-types'
import { getComponentDescendantsBfs, getChildrenCb } from './getComponentDescendants'

type ComponentsIds = Array<string>
type ModelQueries = Array<string>
type BpIdToRowStartToComp = { [bp: string]: { [columnStart: number]: { [rowStart: number]: ComponentsIds } } }

const removeHash = (value: string) => value && value.replace('#', '')

const DEFAULT_BREAKPOINT_KEY = ''

const getBpIdToLayoutData = (layoutData: LayoutData, variantsData: VariantsDataMap, layouts: ModelQueries) =>
	layouts.reduce((acc: { [bp: string]: SingleLayoutItem }, layoutPointer) => {
		const layoutValue = layoutData[removeHash(layoutPointer)]
		if (layoutValue.type !== 'VariantRelation') {
			acc[DEFAULT_BREAKPOINT_KEY] = layoutValue
		} else {
			const layoutVariants: ModelQueries = layoutValue.variants
			const variantType = variantsData[removeHash(layoutVariants[0])].type
			const isDefaultLayoutInBp = layoutVariants.length === 1 && variantType === 'BreakpointRange'

			if (isDefaultLayoutInBp) {
				acc[layoutVariants[0]!] = layoutData[removeHash(layoutValue.to)]
			}
		}
		return acc
	}, {})

const getCompLayoutsPerBpId = (layoutData: LayoutData, variantsData: VariantsDataMap, comp: Component) => {
	const compLayout = layoutData[comp.layoutQuery!]

	if (!compLayout) {
		return {}
	}
	if (compLayout.type !== 'RefArray') {
		return { [DEFAULT_BREAKPOINT_KEY]: compLayout }
	}

	return getBpIdToLayoutData(layoutData, variantsData, compLayout.values)
}
type ColumnAndRowStart = Array<{
	rowStart: number
	columnStart: number
	isHidden: boolean
}>
const getColumnAndRowStart = (compLayout: SingleLayoutData) => {
	const isHidden = !!compLayout.componentLayout?.hidden
	const columnsAndRowsMatrixInCompLayout: ColumnAndRowStart = []
	const gridArea = (compLayout?.itemLayout as GridItemLayout)?.gridArea

	if (isHidden || !gridArea) {
		return columnsAndRowsMatrixInCompLayout
	}

	let rowStart = (gridArea.rowStart || -1) as number
	const rowEnd = (gridArea.rowEnd || -1) as number
	let columnStart = (gridArea.columnStart || -1) as number
	const columnEnd = (gridArea.columnEnd || -1) as number
	for (; columnStart < columnEnd; columnStart++) {
		for (; rowStart < rowEnd; rowStart++) {
			columnsAndRowsMatrixInCompLayout.push({
				rowStart,
				columnStart,
				isHidden,
			})
		}
	}
	return columnsAndRowsMatrixInCompLayout
}

const addRowStartToAcc = (
	acc: BpIdToRowStartToComp,
	breakpointId: string,
	columnStart: number,
	rowStart: number,
	compId: string
) => {
	acc[breakpointId] = acc[breakpointId] || {}
	acc[breakpointId][columnStart] = acc[breakpointId][columnStart] || []
	acc[breakpointId][columnStart][rowStart] = acc[breakpointId][columnStart][rowStart] || []
	acc[breakpointId][columnStart][rowStart].push(compId)
	return acc
}

const getBreakpointOrder = (pageComponent: Component, variantsData: VariantsDataMap) => {
	const breakpointsOrder = ['']
	if (!pageComponent?.breakpointVariantsQuery) {
		return breakpointsOrder
	}
	const breakpointQuery = removeHash(pageComponent.breakpointVariantsQuery)
	return breakpointsOrder.concat(((variantsData[breakpointQuery] as unknown) as UnresolvedBreakpointsData).values)
}

const getCascadedLayout = (
	breakpointOrder: Array<string>,
	cascadedBpIndex: number,
	compDefaultLayoutPerBp: { [bp: string]: SingleLayoutItem }
) => {
	for (let bpIndex = cascadedBpIndex; bpIndex >= 0; bpIndex--) {
		const bpId = breakpointOrder[bpIndex]
		if (compDefaultLayoutPerBp[bpId]) {
			return compDefaultLayoutPerBp[bpId]
		}
	}
}
export const geResponsiveCompsInFirstFold = (
	{ masterPageStructure, structure }: { masterPageStructure: Structure; structure: Structure },
	pageId: string,
	layoutData: LayoutData,
	variantsData: VariantsDataMap,
	firstSectionsThreshold: number = 2
) => {
	const headerSection = Object.keys(masterPageStructure).find(
		(compId) => masterPageStructure[compId].componentType === 'HeaderSection'
	)

	const breakpointOrder = getBreakpointOrder(structure[pageId], variantsData)

	const bpIdToColumnToRowToCompIds = Object.keys(structure).reduce(
		(accBpIdToRowStartToComp: BpIdToRowStartToComp, compId: string) => {
			const comp = structure[compId]
			const isSection = comp.componentType.includes('Section')

			if (!isSection) {
				return accBpIdToRowStartToComp
			}

			const compDefaultLayoutPerBp = getCompLayoutsPerBpId(layoutData, variantsData, comp)

			breakpointOrder.forEach((bp, bpIndex) => {
				const cascadedLayout = getCascadedLayout(breakpointOrder, bpIndex, compDefaultLayoutPerBp)
				if (cascadedLayout) {
					const columnsAndRows = getColumnAndRowStart(cascadedLayout as SingleLayoutData)
					columnsAndRows.forEach((columnAndRow) => {
						accBpIdToRowStartToComp = addRowStartToAcc(
							accBpIdToRowStartToComp,
							bp,
							columnAndRow.columnStart,
							columnAndRow.rowStart,
							compId
						)
					})
				}
			})

			return accBpIdToRowStartToComp
		},
		{}
	)
	const sectionsInFirstFold = Object.keys(bpIdToColumnToRowToCompIds).flatMap((bp) =>
		Object.keys(bpIdToColumnToRowToCompIds[bp]).flatMap((columnStart) =>
			Object.keys(bpIdToColumnToRowToCompIds[bp][(columnStart as unknown) as number])
				.map((rowNum) => Number.parseInt(rowNum, 10))
				.sort((rowNum1, rowNum2) => rowNum1 - rowNum2)
				.slice(0, firstSectionsThreshold)
				.flatMap(
					(rowStart) =>
						bpIdToColumnToRowToCompIds[bp][(columnStart as unknown) as number][
							(rowStart as unknown) as number
						]
				)
		)
	)

	return getComponentDescendantsBfs({
		roots: [headerSection || '', ...sectionsInFirstFold],
		predicate: () => true,
		getChildren: getChildrenCb({ ...masterPageStructure, ...structure }),
		shouldStopUponFalsePredicate: false,
	})
}
